/**
 * What the catalogue contributes to the route tree.
 *
 * Each route declares the permission it needs, and the same declaration drives
 * both the router's gate and the navigation entry — so a menu item cannot
 * appear for a page the user would be refused.
 *
 * The mapping screen and the enrichment queue carry different permissions, and
 * that is not symmetry for its own sake: mapping a family publishes potentially
 * thousands of articles at once, and writing a fiche publishes none.
 *
 * `/catalogue/fiches/$id` carries no `nav` entry: it is a destination reached
 * from the queue, not a menu item.
 */

import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import type { FeatureRoutes } from "@hardauto/core-router";

import { ArticleSetupScreen } from "./screens/ArticleSetupScreen";
import { AttributesScreen } from "./screens/AttributesScreen";
import { AvailabilityScreen } from "./screens/AvailabilityScreen";
import { BrandsScreen } from "./screens/BrandsScreen";
import { CategoriesScreen } from "./screens/CategoriesScreen";
import { ConsultationArticleScreen } from "./screens/ConsultationArticleScreen";
import { ConsultationScreen } from "./screens/ConsultationScreen";
import { FicheScreen } from "./screens/FicheScreen";
import { FichesScreen } from "./screens/FichesScreen";
import { ReliabilityScreen } from "./screens/ReliabilityScreen";
import { CATALOG_PATHS } from "./paths";
import { useLocalScope, type ScopeBinding, type StockStructure } from "./scope";
import "./i18n";

export interface CatalogRoutesOptions {
  /**
   * The depots and the tariffs the availability screen chooses between.
   *
   * Injected by the application: both belong to the referential, and a
   * feature may not import a sibling feature. Absent when not supplied, and
   * the selects then offer only "any depot" and the base price — which is a
   * usable screen rather than a broken one.
   */
  loadSites?: () => Promise<readonly { code: string; name: string }[]>;
  loadScales?: () => Promise<readonly { code: string; label: string }[]>;
  /**
   * The session's context — company, city, point of sale, depot — which the
   * consultation reads and changes. Owned by the application, which shows it
   * in its header; without it the screen keeps a scope of its own.
   */
  useScope?: () => ScopeBinding;
  /** Companies, points of sale and depots, for the consultation's filters. */
  loadStructure?: () => Promise<StockStructure>;
  /** The installation's VAT rate in percent, for the consultation's TTC prices. */
  loadTaxRate?: () => Promise<string>;
}

export function catalogRoutes(
  client: ApiClient,
  options: CatalogRoutesOptions = {},
): FeatureRoutes {
  function Categories() {
    return <CategoriesScreen client={client} />;
  }

  function Availability() {
    return (
      <AvailabilityScreen
        client={client}
        {...(options.loadSites ? { loadSites: options.loadSites } : {})}
        {...(options.loadScales ? { loadScales: options.loadScales } : {})}
      />
    );
  }

  // Chosen once, when the routes are built: a hook must be the same hook on
  // every render, so the choice cannot be made inside the component.
  const useScope = options.useScope ?? useLocalScope;

  function Consultation() {
    const search: { q?: string } = useSearch({ strict: false });
    return (
      <ConsultationScreen
        client={client}
        scope={useScope()}
        loadStructure={options.loadStructure}
        loadTaxRate={options.loadTaxRate}
        initialSearch={search.q}
      />
    );
  }

  function ConsultationArticle() {
    // `strict: false` for the reason `Fiche` below gives.
    const params: { id?: string } = useParams({ strict: false });
    const search: { tab?: string } = useSearch({ strict: false });
    const navigate = useNavigate();
    const { scope } = useScope();
    return (
      <ConsultationArticleScreen
        client={client}
        id={params.id ?? ""}
        scope={scope}
        tab={search.tab}
        onTabChange={(tab) => {
          void navigate({ to: ".", search: { tab }, replace: true });
        }}
      />
    );
  }

  function Fiches() {
    return <FichesScreen client={client} />;
  }

  function Reliability() {
    return <ReliabilityScreen client={client} />;
  }

  function Attributes() {
    return <AttributesScreen client={client} />;
  }

  function Brands() {
    return <BrandsScreen client={client} />;
  }

  function ArticleSetup() {
    return <ArticleSetupScreen client={client} />;
  }

  function Fiche() {
    // `strict: false` because the route tree is assembled at runtime from a
    // registry, so TanStack cannot infer per-route parameter types. The id is
    // validated by the server on every call it is used in.
    const params: { id?: string } = useParams({ strict: false });
    return <FicheScreen client={client} id={params.id ?? ""} />;
  }

  return {
    id: "catalog",
    routes: [
      {
        // The desk's first screen: find an article, see its price and stock.
        path: CATALOG_PATHS.consultation,
        component: Consultation,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.enrichment.read"] },
        nav: {
          group: "catalog:group",
          labelKey: "catalog:nav.consultation",
          section: "main",
          order: 28,
        },
      },
      {
        path: CATALOG_PATHS.consultationArticle,
        component: ConsultationArticle,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.enrichment.read"] },
      },
      {
        // Declared BEFORE `/catalogue/fiches/$id` is irrelevant here — the
        // paths do not overlap — but it is listed first because it is the
        // screen the desk opens when a customer telephones.
        path: CATALOG_PATHS.availability,
        component: Availability,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.enrichment.read"] },
        nav: {
          group: "catalog:group",
          labelKey: "catalog:nav.availability",
          section: "main",
          order: 29,
        },
      },
      {
        path: CATALOG_PATHS.categories,
        component: Categories,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.category.read"] },
        nav: {
          group: "catalog:group",
          labelKey: "catalog:nav.categories",
          section: "main",
          order: 30,
        },
      },
      {
        path: CATALOG_PATHS.attributes,
        component: Attributes,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.category.read"] },
        nav: {
          group: "catalog:group",
          labelKey: "catalog:nav.attributes",
          section: "main",
          order: 30.5,
        },
      },
      {
        path: CATALOG_PATHS.brands,
        component: Brands,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.enrichment.read"] },
        nav: {
          group: "catalog:group",
          labelKey: "catalog:nav.brands",
          section: "main",
          order: 30.6,
        },
      },
      {
        path: CATALOG_PATHS.articleSetup,
        component: ArticleSetup,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.enrichment.read"] },
        nav: {
          group: "catalog:group",
          labelKey: "catalog:nav.articleSetup",
          section: "main",
          order: 30.7,
        },
      },
      {
        path: CATALOG_PATHS.fiches,
        component: Fiches,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.enrichment.read"] },
        nav: {
          group: "catalog:group",
          labelKey: "catalog:nav.fiches",
          section: "main",
          order: 31,
        },
      },
      {
        path: CATALOG_PATHS.fiche,
        component: Fiche,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.enrichment.read"] },
      },
      {
        path: CATALOG_PATHS.reliability,
        component: Reliability,
        layout: "app",
        requirement: { authenticated: true, permissions: ["catalog.reliability.read"] },
        nav: {
          group: "catalog:group",
          labelKey: "catalog:nav.reliability",
          section: "main",
          order: 31.5,
        },
      },
    ],
  };
}
